/**
 * DesignStudioDock — the agent-first canvas for the Design Studio.
 *
 * One conversational box you TYPE into or TALK to. It drives the
 * design.web_studio_director orchestrator: describe a page and it plans the
 * sections, drafts the copy, wires a form, and assembles a DRAFT page — then you
 * refine it in the same box and Publish (gated) when it's ready.
 *
 * Type  → agentsApi.runAgentOrchestrator('design.web_studio_director', {instruction})
 * Speak → browser SpeechRecognition drops a transcript into the box (mic v1).
 * Publish → agentsApi.invokeTool('design.publish_page', {args:{page_id}, approved:true})
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, Mic, Loader2, ArrowUp, Rocket, ExternalLink, Check, ShieldCheck, Square } from 'lucide-react';
import { agentsApi, webPresenceApi } from '../../../shared/services/platformApi';
import { tvTokens } from '../../../shared/components/triveny';
import { config } from '@/src/config';
import { BrowserVoiceSession, VoiceState } from '../lib/browserVoice';
import { runAgentStream } from '../lib/agentStream';

// Human labels for the tools the design agent runs (shown as live progress).
const TOOL_LABELS: Record<string, string> = {
  'design.brand_tokens': 'Setting the brand look',
  'design.analyze_site': 'Analyzing the reference',
  'design.list_sites': 'Finding your site',
  'design.compose_layout': 'Choosing the sections',
  'content.generate_copy': 'Writing the copy',
  'content.generate_seo': 'Writing SEO',
  'design.build_form': 'Building the lead form',
  'design.build_page': 'Assembling the page',
  'design.publish_page': 'Publishing',
};
const toolLabel = (t: string) => TOOL_LABELS[t] || t;

// Non-gated design tools the voice agent may use to build by voice (publish stays gated).
const VOICE_TOOLS = [
  'design.list_sites',
  'design.compose_layout',
  'content.generate_copy',
  'design.build_form',
  'design.build_page',
];

interface DockMessage {
  role: 'user' | 'agent';
  text: string;
  steps?: { tool: string; status?: string; ok?: boolean }[];
  pending?: { tool: string }[];
  pageId?: string | null;
}

const EXAMPLES = [
  'A landing page for our new plan',
  'A webinar signup page with a lead form',
  'A simple pricing page with three tiers',
];

const AGENT_KEY = 'design.web_studio_director';

// Progress stages shown while a build is in flight (the orchestrator runs these steps).
const BUILD_STAGES = [
  'Planning the sections…',
  'Writing the copy…',
  'Curating the layout…',
  'Adding a lead form…',
  'Assembling the page…',
];

function getSpeech(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

function findPageId(result: any): string | null {
  const arts = result?.artifacts;
  if (arts && typeof arts === 'object') {
    for (const v of Object.values(arts)) {
      if (v && typeof v === 'object' && (v as any).page_id) return String((v as any).page_id);
    }
  }
  return null;
}

// Read-only preview of the exact blocks design.build_page writes (hero/text/form/cta).
const _field: React.CSSProperties = {
  padding: '9px 11px',
  border: '1px solid #e2e7f1',
  borderRadius: 7,
  background: '#fff',
  color: '#8a93a8',
  fontSize: 13,
  textAlign: 'left',
};

function BlockView({ block }: { block: any }) {
  const t = block.blockType || block.type || 'text';
  const c = block.content || {};
  const wrap = (children: React.ReactNode, bg?: string) => (
    <section style={{ padding: '30px 26px', borderBottom: '1px solid #eef1f6', background: bg || '#fff' }}>{children}</section>
  );
  switch (t) {
    case 'hero': {
      // Mirror the full editor / public renderer: when a hero background image is set,
      // show it under a light overlay so headline + CTA stay legible (keeps the studio
      // preview consistent with what opens in the Craft editor).
      const heroImg = c.backgroundImage || c.background_image;
      const heroStyle: React.CSSProperties = heroImg
        ? {
            backgroundImage: `linear-gradient(rgba(255,255,255,.78),rgba(255,255,255,.78)), url(${heroImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : { background: '#f5f8ff' };
      return (
        <section style={{ padding: '30px 26px', borderBottom: '1px solid #eef1f6', ...heroStyle }}>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: 27, fontWeight: 700, margin: 0, color: '#131826', lineHeight: 1.15 }}>{c.headline || 'Headline'}</h1>
            {c.subheadline && <p style={{ fontSize: 15.5, color: '#5e6880', margin: '12px 0 0', lineHeight: 1.5 }}>{c.subheadline}</p>}
            {c.cta_primary?.text && (
              <span style={{ display: 'inline-block', marginTop: 18, padding: '10px 20px', background: '#2f5fea', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>
                {c.cta_primary.text}
              </span>
            )}
          </div>
        </section>
      );
    }
    case 'text':
    case 'richtext':
      return wrap(
        <div style={{ fontSize: 14.5, color: '#3a4258', lineHeight: 1.65 }} dangerouslySetInnerHTML={{ __html: c.content || c.text || c.body || '' }} />,
      );
    case 'form':
      return wrap(
        <div style={{ maxWidth: 360, margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ fontSize: 17, fontWeight: 650, margin: '0 0 14px', color: '#131826' }}>{c.title || 'Get started'}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={_field}>Name</div>
            <div style={_field}>Email</div>
            <span style={{ padding: '10px 0', background: '#2f5fea', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>Submit</span>
          </div>
        </div>,
        '#f5f8ff',
      );
    case 'cta':
      return wrap(
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 21, fontWeight: 700, margin: 0, color: '#fff' }}>{c.headline || 'Ready to get started?'}</h2>
          <span style={{ display: 'inline-block', marginTop: 16, padding: '10px 20px', background: '#fff', color: '#2f5fea', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>
            {c.button_text || 'Get started'}
          </span>
        </div>,
        '#2f5fea',
      );
    default:
      return wrap(<div style={{ fontSize: 13, color: '#8a93a8', textTransform: 'capitalize' }}>{String(t).replace(/_/g, ' ')} section</div>);
  }
}

export function DesignStudioDock({ siteId, onPageBuilt }: { siteId?: string; onPageBuilt?: (pageId: string) => void }) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<DockMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [pageId, setPageId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const speechSupported = !!getSpeech();
  const voiceRef = useRef<BrowserVoiceSession | null>(null);
  const [voiceState, setVoiceState] = useState<VoiceState | null>(null);
  const voiceActive = voiceState === 'connecting' || voiceState === 'live' || voiceState === 'listening' || voiceState === 'speaking';
  // buildKey forces the right-panel builder iframe to reload each time a page is (re)built.
  const [buildKey, setBuildKey] = useState(0);
  const setPage = (pid: string) => {
    setPageId(pid);
    setBuildKey((k) => k + 1);
    // Tell the parent (WebPresencePage) so the Pages tab list picks up the new draft.
    onPageBuilt?.(pid);
  };
  // Live per-step progress (streamed) + the read-only preview blocks for the right pane.
  const [liveSteps, setLiveSteps] = useState<{ tool: string; done: boolean; ok?: boolean }[]>([]);
  const [previewBlocks, setPreviewBlocks] = useState<any[]>([]);

  useEffect(() => () => voiceRef.current?.stop(), []);

  // Fetch the page's blocks for the read-only preview whenever a build lands/refines.
  useEffect(() => {
    if (!pageId) {
      setPreviewBlocks([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const res: any = await webPresenceApi.getPage(pageId);
        const blocks = (res?.data ?? res)?.content_blocks;
        if (!cancel) setPreviewBlocks(Array.isArray(blocks) ? blocks : []);
      } catch {
        if (!cancel) setPreviewBlocks([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [pageId, buildKey]);

  // Progress tracker while a build is running (typed path). Elapsed seconds + staged label.
  const [elapsed, setElapsed] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  useEffect(() => {
    if (!busy) {
      setElapsed(0);
      setStageIdx(0);
      return;
    }
    const t0 = Date.now();
    const iv = setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setElapsed(s);
      setStageIdx(Math.min(BUILD_STAGES.length - 1, Math.floor(s / 4)));
    }, 500);
    return () => clearInterval(iv);
  }, [busy]);

  const stopVoice = () => {
    voiceRef.current?.stop();
    voiceRef.current = null;
    setVoiceState(null);
  };

  const startVoice = async () => {
    if (voiceRef.current) {
      stopVoice();
      return;
    }
    setVoiceState('connecting'); // instant feedback while the session is minted
    try {
      const res: any = await agentsApi.createBrowserVoiceSession({
        agent_key: AGENT_KEY,
        context: { tools: VOICE_TOOLS, opening: "Hi, I can build your page. What would you like to make?" },
      } as any);
      const d = res?.data ?? res;
      if (d?.streaming && d?.session_id) {
        const base = String((config as any)?.weaveinApi?.baseUrl || '').replace(/\/$/, '');
        const wsUrl = base.replace(/^http/, 'ws') + `/api/webhooks/voice-agent/browser-stream?session_id=${encodeURIComponent(d.session_id)}`;
        const vs = new BrowserVoiceSession(wsUrl, {
          onCaption: (role, text) => setMessages((m) => [...m, { role: role === 'user' ? 'user' : 'agent', text }]),
          onArtifact: (pid) => setPage(pid),
          onState: (s) => {
            setVoiceState(s);
            if (s === 'ended') { voiceRef.current = null; setVoiceState(null); }
            if (s === 'error') { voiceRef.current = null; setVoiceState(null); toggleMic(); }
          },
        });
        voiceRef.current = vs;
        setVoiceState('connecting');
        void vs.start();
      } else {
        // Deepgram not configured → fall back to browser speech (mic v1)
        setVoiceState(null);
        toggleMic();
      }
    } catch {
      setVoiceState(null);
      toggleMic();
    }
  };

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (listening) recRef.current?.stop();
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    setLiveSteps([]);
    const instruction = pageId
      ? `Continue refining the draft page you already created (page_id ${pageId}). ${text}`
      : text;
    let gotSummary = false;
    let lastPid: string | null = null;
    try {
      await runAgentStream(AGENT_KEY, { instruction }, (ev) => {
        if (ev.type === 'step') {
          if (ev.status === 'running') {
            setLiveSteps((s) => [...s, { tool: ev.tool, done: false }]);
          } else {
            setLiveSteps((s) => {
              const next = [...s];
              const idx = next.map((x) => x.tool).lastIndexOf(ev.tool);
              if (idx >= 0) next[idx] = { tool: ev.tool, done: true, ok: ev.ok };
              return next;
            });
          }
        } else if (ev.type === 'artifact') {
          lastPid = ev.page_id;
          setPage(ev.page_id);
        } else if (ev.type === 'summary') {
          gotSummary = true;
          setMessages((m) => [
            ...m,
            {
              role: 'agent',
              text: ev.summary || 'Done.',
              pending: Array.isArray(ev.pending_approvals) ? ev.pending_approvals : undefined,
              pageId: lastPid,
            },
          ]);
        } else if (ev.type === 'error') {
          setMessages((m) => [...m, { role: 'agent', text: `The assistant hit a problem: ${ev.error}` }]);
        }
      });
      if (!gotSummary) {
        setMessages((m) => [...m, { role: 'agent', text: 'Done.' }]);
      }
    } catch {
      setMessages((m) => [...m, { role: 'agent', text: 'Something went wrong reaching the assistant.' }]);
    } finally {
      setBusy(false);
      setLiveSteps([]);
    }
  };

  const toggleMic = () => {
    const SR = getSpeech();
    if (!SR) return;
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    let finalText = '';
    rec.onresult = (e: any) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setInput((finalText + ' ' + interim).trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const publish = async () => {
    if (!pageId || publishing) return;
    setPublishing(true);
    try {
      const res: any = await agentsApi.invokeTool('design.publish_page', {
        args: { page_id: pageId },
        approved: true,
      });
      const d = res?.data ?? res;
      setMessages((m) => [
        ...m,
        { role: 'agent', text: d?.published ? 'Published — your page is live. 🎉' : d?.error || d?.message || 'Could not publish the page.' },
      ]);
    } catch {
      setMessages((m) => [...m, { role: 'agent', text: 'Could not publish the page.' }]);
    } finally {
      setPublishing(false);
    }
  };

  const empty = messages.length === 0;

  const building = busy; // typed-build progress; voice feedback is captions + the page loading

  return (
    <div style={S.split}>
      <style>{`@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(1.4); } }`}</style>

      {/* LEFT — the conversation */}
      <div style={S.left}>
        <div style={S.leftBody}>
          {empty ? (
            <div style={S.emptyLeft}>
              <p style={S.prompt}>
                <Sparkles size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: tvTokens.accentInk }} />
                Describe the page you want to build — or tap the mic and talk it through.
              </p>
              <div style={S.examples}>
                {EXAMPLES.map((ex) => (
                  <button key={ex} onClick={() => setInput(ex)} style={S.exampleChip}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div ref={threadRef} style={S.thread}>
              {messages.map((m, i) => (
                <div key={i} style={m.role === 'user' ? S.userRow : S.agentRow}>
                  <div style={m.role === 'user' ? S.userBubble : S.agentBubble}>
                    {m.text}
                    {m.steps && m.steps.length > 0 && (
                      <div style={S.steps}>
                        {m.steps.map((s, j) => (
                          <span key={j} style={S.stepChip} title={s.status || ''}>
                            {s.ok ? <Check size={11} color={tvTokens.success || '#1f9663'} /> : <span style={S.stepDot} />}
                            {s.tool}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.pending && m.pending.length > 0 && (
                      <div style={S.pending}>
                        <ShieldCheck size={12} /> Needs your approval: {m.pending.map((p) => p.tool).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div style={S.agentRow}>
                  <div style={{ ...S.agentBubble, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {liveSteps.length === 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        {BUILD_STAGES[stageIdx]} <span style={{ opacity: 0.55 }}>{elapsed}s</span>
                      </span>
                    ) : (
                      liveSteps.map((s, j) => (
                        <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                          {s.done ? (
                            s.ok === false ? (
                              <Square size={11} color="#cc3b57" />
                            ) : (
                              <Check size={13} color={tvTokens.success || '#1f9663'} />
                            )
                          ) : (
                            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: tvTokens.accentInk }} />
                          )}
                          <span style={{ color: s.done ? tvTokens.ink500 : tvTokens.ink }}>{toolLabel(s.tool)}</span>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {voiceActive && (
          <div style={S.voiceBar}>
            <span style={{ ...S.voiceDot, animation: voiceState === 'speaking' ? 'none' : 'pulse 1.4s ease-in-out infinite' }} />
            <span style={{ flex: 1 }}>
              {voiceState === 'connecting'
                ? 'Connecting…'
                : voiceState === 'speaking'
                  ? 'Assistant speaking…'
                  : 'Listening — just talk'}
            </span>
            <button onClick={stopVoice} style={S.voiceStop}>
              <Square size={11} /> Stop
            </button>
          </div>
        )}

        <div style={S.composer}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send();
            }}
            placeholder={listening ? 'Listening… speak now' : 'Describe or refine your page…'}
            rows={2}
            style={S.textarea}
          />
          <div style={S.composerActions}>
            <button
              onClick={startVoice}
              title={voiceActive ? 'Stop voice' : listening ? 'Stop listening' : 'Talk it through'}
              style={{ ...S.micBtn, ...(voiceActive || listening ? S.micBtnActive : {}) }}
            >
              {voiceActive ? <Square size={16} /> : <Mic size={19} />}
            </button>
            <button onClick={send} disabled={!input.trim() || busy} style={{ ...S.sendBtn, opacity: !input.trim() || busy ? 0.5 : 1 }}>
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT — the page being built */}
      <div style={S.right}>
        <div style={S.rightHead}>
          <span style={S.rightTitle}>{pageId ? 'Your page' : 'Preview'}</span>
          {pageId && (
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={`/design/v2?page=${encodeURIComponent(pageId)}`} target="_blank" rel="noreferrer" style={S.secondaryBtn}>
                <ExternalLink size={13} /> Open full editor
              </a>
              <button onClick={publish} disabled={publishing} style={{ ...S.primaryBtn, opacity: publishing ? 0.6 : 1 }}>
                {publishing ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Rocket size={13} />}
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          )}
        </div>
        <div style={S.rightBody}>
          {pageId && previewBlocks.length > 0 ? (
            <div style={S.previewScroll}>
              {previewBlocks.map((b, i) => (
                <BlockView key={b.id || i} block={b} />
              ))}
            </div>
          ) : (
            <div style={S.rightEmpty}>
              <Sparkles size={30} color={tvTokens.accentInk} style={{ opacity: 0.7 }} />
              <p style={{ margin: '12px 0 0', maxWidth: 300 }}>
                {pageId
                  ? 'Preparing the preview…'
                  : 'Your page appears here as the assistant builds it — sections, copy, and a lead form.'}
              </p>
            </div>
          )}
          {building && (
            <div style={S.buildingOverlay}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span>
                {liveSteps.length > 0 ? toolLabel(liveSteps[liveSteps.length - 1].tool) : BUILD_STAGES[stageIdx]}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  split: {
    display: 'flex',
    gap: 16,
    alignItems: 'stretch',
    height: 'calc(100dvh - 176px)',
    minHeight: 420,
    maxHeight: 'calc(100dvh - 120px)',
    overflow: 'hidden', // panels scroll internally; nothing escapes onto the footer
  },
  left: {
    flex: '1 1 40%',
    maxWidth: 560,
    minWidth: 280,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minHeight: 0,
    overflow: 'hidden',
  },
  leftBody: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' },
  emptyLeft: { margin: 'auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' },
  right: {
    flex: '2 1 460px',
    minWidth: 300,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tvTokens.border}`,
    borderRadius: 14,
    overflow: 'hidden',
    background: tvTokens.paper2,
    minHeight: 0,
  },
  rightHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '10px 14px',
    borderBottom: `1px solid ${tvTokens.border}`,
    background: tvTokens.surface || '#fff',
  },
  rightTitle: { fontSize: 13, fontWeight: 600, color: tvTokens.ink },
  rightBody: { flex: 1, position: 'relative', minHeight: 0, background: '#fff' },
  previewScroll: { position: 'absolute', inset: 0, overflowY: 'auto', background: '#fff' },
  iframe: { width: '100%', height: '100%', border: 'none', display: 'block' },
  rightEmpty: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: tvTokens.ink500,
    fontSize: 14,
    padding: 24,
  },
  buildingOverlay: {
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12.5,
    fontWeight: 500,
    color: tvTokens.accentInk,
    background: tvTokens.surface || '#fff',
    border: `1px solid ${tvTokens.border}`,
    borderRadius: 999,
    padding: '6px 13px',
    boxShadow: '0 2px 10px rgba(20,22,34,0.08)',
  },
  prompt: { textAlign: 'center', fontSize: 15.5, color: tvTokens.ink500, margin: '0 auto', maxWidth: 440, lineHeight: 1.5 },
  hero: { textAlign: 'center', padding: '18px 0 4px' },
  eyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: tvTokens.fontMono,
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 600,
    color: tvTokens.accentInk,
    marginBottom: 10,
  },
  title: { fontFamily: tvTokens.fontDisplay, fontSize: 30, fontWeight: 700, color: tvTokens.ink, margin: 0, letterSpacing: '-0.02em' },
  subtitle: { fontSize: 15, color: tvTokens.ink500, margin: '10px auto 0', maxWidth: 480, lineHeight: 1.5 },
  thread: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', padding: '4px 2px' },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  agentRow: { display: 'flex', justifyContent: 'flex-start' },
  userBubble: {
    background: tvTokens.accentInk,
    color: '#fff',
    padding: '10px 14px',
    borderRadius: '14px 14px 4px 14px',
    fontSize: 14,
    maxWidth: '82%',
    lineHeight: 1.45,
  },
  agentBubble: {
    background: tvTokens.paper2,
    color: tvTokens.ink,
    border: `1px solid ${tvTokens.border}`,
    padding: '10px 14px',
    borderRadius: '14px 14px 14px 4px',
    fontSize: 14,
    maxWidth: '88%',
    lineHeight: 1.5,
  },
  steps: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 },
  stepChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontFamily: tvTokens.fontMono,
    fontSize: 10.5,
    color: tvTokens.ink500,
    background: tvTokens.paper,
    border: `1px solid ${tvTokens.border}`,
    padding: '2px 7px',
    borderRadius: 999,
  },
  stepDot: { width: 6, height: 6, borderRadius: '50%', background: tvTokens.accent, display: 'inline-block' },
  pending: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 8,
    fontSize: 12,
    color: '#b9781f',
    background: '#f8efda',
    padding: '4px 9px',
    borderRadius: 6,
  },
  draftCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    background: tvTokens.paper2,
    border: `1px solid ${tvTokens.border}`,
    borderRadius: 12,
    padding: '12px 16px',
  },
  examples: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  exampleChip: {
    fontSize: 13,
    color: tvTokens.ink500,
    background: tvTokens.surface || '#fff',
    border: `1px solid ${tvTokens.border}`,
    padding: '8px 13px',
    borderRadius: 999,
    cursor: 'pointer',
  },
  composer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    background: tvTokens.surface || '#fff',
    border: `1.5px solid ${tvTokens.border}`,
    borderRadius: 14,
    padding: 10,
    boxShadow: '0 2px 12px rgba(20,22,34,0.05)',
  },
  textarea: {
    flex: 1,
    border: 'none',
    outline: 'none',
    resize: 'none',
    background: 'transparent',
    fontFamily: tvTokens.fontSans,
    fontSize: 15,
    color: tvTokens.ink,
    lineHeight: 1.5,
    padding: '4px 6px',
  },
  composerActions: { display: 'flex', alignItems: 'center', gap: 6 },
  micBtn: {
    width: 42,
    height: 42,
    borderRadius: 11,
    border: '1px solid #d9c9f3',
    background: '#f6f1fd',
    color: '#7a46c9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  micBtnActive: { background: '#7a46c9', color: '#fff', borderColor: '#7a46c9' },
  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: 'none',
    background: tvTokens.accentInk,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    background: tvTokens.accentInk,
    border: 'none',
    borderRadius: 8,
    padding: '8px 13px',
    cursor: 'pointer',
  },
  secondaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    color: tvTokens.ink,
    background: 'transparent',
    border: `1px solid ${tvTokens.border}`,
    borderRadius: 8,
    padding: '8px 13px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  hint: { textAlign: 'center', fontSize: 11.5, color: tvTokens.ink400 || '#8a93a8' },
  voiceBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#f1eafb',
    border: '1px solid #d9c9f3',
    borderRadius: 10,
    padding: '9px 14px',
    fontSize: 13,
    color: '#5a3a9c',
    fontWeight: 500,
  },
  voiceDot: { width: 9, height: 9, borderRadius: '50%', background: '#7a46c9', flex: 'none' },
  voiceStop: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    color: '#7a46c9',
    background: '#fff',
    border: '1px solid #d9c9f3',
    borderRadius: 7,
    padding: '5px 10px',
    cursor: 'pointer',
  },
};

export default DesignStudioDock;
